import { ModelEntityType } from '@upright/common/model/input/types';
import { sdgAlignmentLevelToValence } from '@upright/common/model/output/scores/sdg-alignment/shared';
import {
  SdgAlignmentByLevel,
  SdgAlignmentLevel,
  SdgAlignmentValence,
  SdgTarget,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { inlineSelectStyles } from '@upright/ui/common/generic/utilities/selectStyles';
import { getSdgTargetDescription } from '@upright/common/model/output/scores/sdg-alignment/sdg-targets';
import { uniq, values, flatten, compact, pickBy } from 'lodash';
import { ReactNode } from 'react';
import Select, { OnChangeValue } from 'react-select';

interface Props {
  selected: SdgTarget | undefined;
  valence: SdgAlignmentValence;
  onChange: (target: SdgTarget | undefined) => void;
  data: SdgAlignmentByLevel;
  entityType: ModelEntityType;
}

interface OptionType {
  label: ReactNode;
  value: SdgTarget;
}

function getAlignedTargets(data: SdgAlignmentByLevel, valence: SdgAlignmentValence) {
  function sortComparatorValue(target: SdgTarget) {
    return parseInt(target.replace('.', ''), 10);
  }

  const filteredData: SdgAlignmentByLevel = pickBy(data, (_: any, level: SdgAlignmentLevel) => {
    return sdgAlignmentLevelToValence(level) === valence;
  });

  const targets = uniq(flatten(compact(values(filteredData).map(d => d.targets))));
  return targets.sort((a, b) => sortComparatorValue(a) - sortComparatorValue(b));
}

export function SdgDrillDownTargetSelector(props: Props) {
  const targets = getAlignedTargets(props.data, props.valence);
  const options = targets.map(target => {
    return {
      label: (
        <>
          <b>Target {target}:</b> {getSdgTargetDescription(target)}
        </>
      ),
      value: target,
    };
  });
  const isCompany = props.entityType === ModelEntityType.COMPANY;
  const alignedText = props.valence === SdgAlignmentValence.ALIGNED ? 'aligned' : 'misaligned';
  const placeholder = isCompany
    ? `Show only products ${alignedText} with target...`
    : `Show only contituents ${alignedText} with target...`;
  const selectedOption = options.find(o => o.value === props.selected);
  return (
    <Select
      placeholder={placeholder}
      value={selectedOption}
      styles={inlineSelectStyles}
      options={options}
      isClearable={true}
      isMulti={false}
      onChange={(selection: OnChangeValue<OptionType, false>) => {
        props.onChange(selection ? selection.value : undefined);
      }}
    />
  );
}
